/**
 * Level configuration data for Pacman game
 * Handles difficulty progression across levels
 */
export interface LevelConfig {
  level: number;
  ghostSpeedMultiplier: number;
  pacmanSpeedMultiplier: number;
  powerPelletDuration: number;
  ghostFrightenedSpeedMultiplier: number;
  ghostDeadSpeedMultiplier: number;
}

/**
 * LevelConfigManager - Manages level-specific configurations
 */
export class LevelConfigManager {
  private static readonly BASE_GHOST_SPEED = 80; // Our current base speed
  private static readonly BASE_PACMAN_SPEED = 130; // Our current base speed

  /**
   * Get configuration for a specific level
   */
  public static getLevelConfig(level: number): LevelConfig {
    // Level 1-3: Easy
    if (level >= 1 && level <= 3) {
      return {
        level,
        ghostSpeedMultiplier: 0.8, // 80% of base speed
        pacmanSpeedMultiplier: 1.0, // Keep Pacman at base speed
        powerPelletDuration: 6.0, // 6 seconds
        ghostFrightenedSpeedMultiplier: 0.6, // 60% of normal speed when frightened
        ghostDeadSpeedMultiplier: 1.5, // 150% of normal speed when dead
      };
    }

    // Level 4-6: Medium
    if (level >= 4 && level <= 6) {
      return {
        level,
        ghostSpeedMultiplier: 0.9, // 90% of base speed
        pacmanSpeedMultiplier: 1.0,
        powerPelletDuration: 5.0, // 5 seconds
        ghostFrightenedSpeedMultiplier: 0.6,
        ghostDeadSpeedMultiplier: 1.5,
      };
    }

    // Level 7-9: Hard
    if (level >= 7 && level <= 9) {
      return {
        level,
        ghostSpeedMultiplier: 1.0, // 100% of base speed
        pacmanSpeedMultiplier: 1.0,
        powerPelletDuration: 4.0, // 4 seconds
        ghostFrightenedSpeedMultiplier: 0.6,
        ghostDeadSpeedMultiplier: 1.5,
      };
    }

    // Level 10+: Very Hard (cap)
    return {
      level,
      ghostSpeedMultiplier: 1.0, // 100% of base speed
      pacmanSpeedMultiplier: 1.0,
      powerPelletDuration: 3.0, // 3 seconds
      ghostFrightenedSpeedMultiplier: 0.6,
      ghostDeadSpeedMultiplier: 1.5,
    };
  }

  /**
   * Get calculated ghost speed for a level
   */
  public static getGhostSpeed(level: number): number {
    const config = this.getLevelConfig(level);
    return Math.round(this.BASE_GHOST_SPEED * config.ghostSpeedMultiplier);
  }

  /**
   * Get calculated Pacman speed for a level
   */
  public static getPacmanSpeed(level: number): number {
    const config = this.getLevelConfig(level);
    return Math.round(this.BASE_PACMAN_SPEED * config.pacmanSpeedMultiplier);
  }

  /**
   * Get power pellet duration for a level
   */
  public static getPowerPelletDuration(level: number): number {
    const config = this.getLevelConfig(level);
    return config.powerPelletDuration;
  }
}
